/* eslint-disable max-len */
import React, { Component } from 'react';
import {
  Table, Header, Icon, Button, Select, Grid,
} from 'semantic-ui-react';
import { replace } from 'react-router-redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { fetchM, serverRequestGetBlob } from '../../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../../actions/flash';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';
import DOCUMENT_TYPE_OPTIONS, { DOCUMENT_TYPE_LABELS, getDocumentTypeLabel } from '../../../constants/documentType';
import { isVietnamCompany } from '../../../utils/vietnam';
import config from '../../../../config';
import AddDocument from '../../modals/AddSharedMedia';
import DeleteConfirm from '../../modals/DeleteConfirm';
import ViewLarge from '../../modals/ViewLarge';
import SearchFilter from '../../../components/SearchFilter';
import Timestamp from '../../../components/Timestamp';
import { decodeSearch, encodeSearch } from '../../../utils/url';
import downloadIcon from '../../../assets/icons/download.svg';
import deleteIcon from '../../../assets/icons/delete.svg';
import uploadIcon from '../../../assets/images/icons/upload.svg';
import {
  detectSnapshotMediaType,
  getSnapshotIdentityDocuments,
  isGeneratedIdentityMirror,
  snapshotDownloadOnlyExtension,
  snapshotFileName,
  snapshotIsReady,
} from './snapshotIdentityDocuments';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff'];

class Documents extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    documentData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fileName: PropTypes.string,
      fileType: PropTypes.string,
      fileSizeBytes: PropTypes.number,
      uploaderName: PropTypes.string,
      createdAt: PropTypes.string,
    }),
    ),
    // A frozen CRM opportunity supplies read-only identity snapshot rows for Vietnam only.
    opportunity: PropTypes.object,
    // Booking Documents supplies these current-purchaser rows; standalone Opportunity Documents
    // continues to use the frozen opportunity snapshot path.
    currentIdentityMode: PropTypes.bool,
    currentIdentityDocuments: PropTypes.arrayOf(PropTypes.object),
    currentIdentityBookingID: PropTypes.string,
    loading: PropTypes.bool,
    id: PropTypes.string,
    // CPI-1249: shared with the Sales Overview Documents tab, which files against another parent.
    parentType: PropTypes.string,
    parentID: PropTypes.string,
    mediaToken: PropTypes.string,
    auth: PropTypes.shape({
      isHeadquarters: PropTypes.bool,
    }),
    location: PropTypes.shape({
      query: PropTypes.object,
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    fetchDocument: PropTypes.func,
    t: PropTypes.func,
  };

  static defaultProps = {
    parentType: 'opportunity',
  };

  constructor(props) {
    super(props);
    this.mounted = false;
    this.snapshotRequestVersion = 0;
    this.documentRequestVersion = 0;
    this.initialDocumentDataKey = this.documentQueryKey(decodeSearch((props.location && props.location.search) || ''));
    this.state = {
      showConfirm: false,
      showLarge: false,
      selectedMedia: null,
      selectedDocument: null,
      showAddMedia: false,
      selectedSnapshotObjectURL: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps) {
    const previous = prevProps.opportunity || prevProps.data || {};
    const current = this.props.opportunity || this.props.data || {};
    const previousParentID = prevProps.parentID || prevProps.id;
    const currentParentID = this.props.parentID || this.props.id;
    const query = decodeSearch((this.props.location && this.props.location.search) || '');
    const currentKey = this.documentQueryKey(query);
    if ((prevProps.location && prevProps.location.search) !== (this.props.location && this.props.location.search) && this.state.documentLoadError) {
      this.setState({ documentLoadError: false });
    }
    if (this.pendingDocumentDataKey && this.pendingDocumentDataKey !== currentKey) {
      this.documentRequestVersion += 1;
      this.pendingDocumentDataKey = null;
      this.setState({ loading: false, documentLoadError: false });
    }
    if ((prevProps.location && prevProps.location.search) !== (this.props.location && this.props.location.search) ||
      previousParentID !== currentParentID || prevProps.parentType !== this.props.parentType || prevProps.documentData !== this.props.documentData) {
      if ((this.state.documentDataKey !== this.documentQueryKey(query) && this.pendingDocumentDataKey !== this.documentQueryKey(query)) || prevProps.documentData !== this.props.documentData) {
        this.replaceQuery(query, false);
      }
    }
    if (previous.id !== current.id ||
      previous.snapshot_frozen_at !== current.snapshot_frozen_at ||
      previousParentID !== currentParentID || prevProps.parentType !== this.props.parentType ||
      prevProps.currentIdentityBookingID !== this.props.currentIdentityBookingID ||
      prevProps.currentIdentityMode !== this.props.currentIdentityMode ||
      JSON.stringify(prevProps.currentIdentityDocuments || []) !== JSON.stringify(this.props.currentIdentityDocuments || [])) {
      this.snapshotRequestVersion += 1;
      if (this.state.selectedSnapshotObjectURL) {
        URL.revokeObjectURL(this.state.selectedSnapshotObjectURL);
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ showLarge: false, selectedMedia: null, selectedSnapshotObjectURL: null });
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.documentRequestVersion += 1;
    this.snapshotRequestVersion += 1;
    if (this.state.selectedSnapshotObjectURL) URL.revokeObjectURL(this.state.selectedSnapshotObjectURL);
  }

  // Helper to get the right icon based on file extension
  getFileIcon = (fileName = '') => {
    const ext = fileName.split('.').pop().toLowerCase();

    if (ext === 'pdf') return 'file pdf outline';
    if (IMAGE_EXTENSIONS.includes(ext)) return 'file image outline';
    if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) return 'file word outline';
    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'file excel outline';
    if (['ppt', 'pptx'].includes(ext)) return 'file powerpoint outline';
    if (ext === 'txt') return 'file text outline';

    return 'file alternate outline';
  };

  getDocumentURL = doc => `${config.get('bookApiURL')}/shared_medias/${doc.id}/file/${encodeURIComponent(doc.fileName)}`;

  isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.split('.').pop().toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
  };

  isProtectedIdentityDocument = doc => !!(doc && (doc.snapshotDocument || doc.currentIdentityDocument));

  isProtectedIdentityUnavailable = doc => !!(doc && (
    doc.snapshotUnavailable || doc.currentIdentityUnavailable || doc.currentIdentityPending
  ));

  protectedIdentityURL = doc => doc.snapshotURL || doc.identityURL;

  handleOpenDocument = (e, doc) => {
    if (this.isProtectedIdentityDocument(doc)) {
      if (e && e.preventDefault) e.preventDefault();
      this.openProtectedIdentityDocument(doc);
      return;
    }
    // A manual image can be opened while a snapshot fetch is pending. It becomes the active
    // preview, so the late snapshot result must not overwrite it.
    this.snapshotRequestVersion += 1;
    if (this.state.selectedSnapshotObjectURL) {
      URL.revokeObjectURL(this.state.selectedSnapshotObjectURL);
      this.setState({ selectedSnapshotObjectURL: null });
    }
    // Image types open in the existing ViewLarge modal; everything else
    // (and unknown extensions) falls through to the link's default
    // target="_blank" behaviour, opening in a new tab.
    if (this.isImageFile(doc.fileName)) {
      if (e && e.preventDefault) e.preventDefault();
      this.setState({
        showLarge: true,
        selectedMedia: {
          url: this.getDocumentURL(doc),
          label: doc.fileName,
        },
      });
    }
  }

  snapshotRequestKey = doc => {
    const opportunity = this.props.opportunity || this.props.data || {};
    const parent = this.getParent();
    return [parent.parentType, parent.parentID, opportunity.id, opportunity.snapshot_frozen_at, this.props.currentIdentityBookingID, doc.customerID, this.protectedIdentityURL(doc)].join('\u0000');
  }

  handleCloseLarge = () => {
    this.snapshotRequestVersion += 1;
    if (this.state.selectedSnapshotObjectURL) URL.revokeObjectURL(this.state.selectedSnapshotObjectURL);
    this.setState({ showLarge: false, selectedMedia: null, selectedSnapshotObjectURL: null });
  }

  handleShowAddMedia = (e) => {
    if (e) e.preventDefault();
    this.setState({
      showAddMedia: true,
    });
  }

  handleCloseAddMedia = (e) => {
    if (e) e.preventDefault();
    this.setState({
      showAddMedia: false,
    });
  }

  formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = parseFloat((bytes / (k ** i)).toFixed(dm));

    return `${value} ${sizes[i]}`;
  }

  getUniqueDocuments = (documents) => {
    if (!Array.isArray(documents)) return [];

    const seen = new Set();

    return documents.filter((doc) => {
      const fileType = (doc && doc.fileType) != null ? doc.fileType : '';
      const fileName = (doc && doc.fileName) != null ? doc.fileName : '';
      const fileSizeBytes = (doc && doc.fileSizeBytes) != null ? doc.fileSizeBytes : '';
      const key = `${fileType}\u0000${fileName}\u0000${fileSizeBytes}`;

      // Keep rows with no identifying metadata because they cannot be safely deduped.
      if (!fileType && !fileName && fileSizeBytes === '') {
        return true;
      }
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }

  getParent = () => {
    const { parentType, parentID, id } = this.props;
    return {
      parentType: parentType || 'opportunity',
      parentID: parentID || id,
    };
  }

  addDocument = async () => {
    const { store } = this.context;
    const { files } = this.state;
    const { mediaToken, t } = this.props;
    const { parentType, parentID } = this.getParent();

    if (!files || files.length === 0 || !parentID) return;

    const uploadPromises = files.map((fileWrapper) => {
      const actualBinary = fileWrapper.file;
      const data = new FormData();

      data.append('file', actualBinary);

      const metadata = {
        parentType,
        parentID,
        fileName: actualBinary.name || fileWrapper.fileName,
        fileType: fileWrapper.fileType,
        sourceApp: 'mhub-book-api',
      };

      data.append('metadata', JSON.stringify(metadata));

      const action = fetchM('/shared_medias/', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'X-CSRF-Token': mediaToken,
        },
        body: data,
      });

      return store.dispatch(action)
        .then(() => {
          store.dispatch(flashSuccessMessage({
            header: t('Success', { ns: TRANSLATION_FILES.constants }),
            message: t('FILE_UPLOAD_SUCCESS', { name: actualBinary.name, ns: TRANSLATION_FILES.screens }),
          }));
        })
        .catch(async (err) => {
          let backendMessage = t('FILE_UPLOAD_FAILED', { name: actualBinary.name, ns: TRANSLATION_FILES.screens });

          if (err.response && typeof err.response.text === 'function') {
            try {
              const errorText = await err.response.text();
              if (errorText) backendMessage = errorText;
            } catch (e) {
              // ignore parse error, use default message
            }
          }

          store.dispatch(flashErrorMessage({
            header: t('Upload Failed', { ns: TRANSLATION_FILES.screens }),
            message: backendMessage,
          }));
        });
    });

    await Promise.all(uploadPromises);
    if (this.mounted) {
      this.setState({ files: null });
      if (this.props.fetchDocument) {
        this.props.fetchDocument();
      }
    }
  }

  handleOnUpload = (event, { files }) => {
    event.preventDefault();
    this.setState({
      showAddMedia: false,
      files,
    }, () => {
      this.addDocument();
    });
  }

  handleDownload = (e, selectedDocument) => {
    if (e && e.preventDefault) e.preventDefault();
    if (this.isProtectedIdentityDocument(selectedDocument)) {
      this.openProtectedIdentityDocument(selectedDocument, true);
      return;
    }
    this.downloadDocument(selectedDocument.id, selectedDocument.fileName);
  }

  readSnapshotHeader = (blob) => {
    const header = blob.slice(0, 16);
    if (typeof header.arrayBuffer === 'function') {
      return header.arrayBuffer().then(bytes => new Uint8Array(bytes));
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = reject;
      reader.readAsArrayBuffer(header);
    });
  }

  downloadObjectURL = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  loadProtectedIdentityMedia = async (doc) => {
    const { store } = this.context;
    const blob = await serverRequestGetBlob(this.protectedIdentityURL(doc), store);
    if (!blob) return null;

    const detected = detectSnapshotMediaType(await this.readSnapshotHeader(blob));
    const fallbackExtension = snapshotDownloadOnlyExtension(doc.snapshotExtension || (doc.fileName || '').split('.').pop().toLowerCase());
    if (!detected && !fallbackExtension) return null;

    const extension = detected ? detected.extension : fallbackExtension;
    const mimeType = detected ? detected.mimeType : 'application/octet-stream';
    return {
      fileName: snapshotFileName(doc.fileName, extension),
      objectURL: URL.createObjectURL(new Blob([blob], { type: mimeType })),
      previewable: !!(detected && detected.previewable),
      pdf: !!(detected && detected.pdf),
    };
  }

  openProtectedIdentityDocument = async (doc, download = false) => {
    const { store } = this.context;
    const requestKey = this.snapshotRequestKey(doc);
    const requestVersion = this.snapshotRequestVersion + 1;
    this.snapshotRequestVersion = requestVersion;
    let media;
    try {
      media = await this.loadProtectedIdentityMedia(doc);
    } catch (err) {
      if (this.mounted && requestVersion === this.snapshotRequestVersion && requestKey === this.snapshotRequestKey(doc)) {
        store.dispatch(flashErrorMessage({ message: 'Identity document is unavailable.' }));
      }
      return;
    }
    if (!this.mounted || requestVersion !== this.snapshotRequestVersion || requestKey !== this.snapshotRequestKey(doc)) {
      if (media && media.objectURL) URL.revokeObjectURL(media.objectURL);
      return;
    }
    if (!media) {
      store.dispatch(flashErrorMessage({ message: 'Identity document is unavailable.' }));
      return;
    }

    if (download || !media.previewable) {
      this.downloadObjectURL(media.objectURL, media.fileName);
      window.setTimeout(() => URL.revokeObjectURL(media.objectURL), 60000);
      return;
    }

    if (this.state.selectedSnapshotObjectURL) URL.revokeObjectURL(this.state.selectedSnapshotObjectURL);
    this.setState({
      showLarge: true,
      selectedSnapshotObjectURL: media.objectURL,
      selectedMedia: { url: media.objectURL, label: media.fileName },
    });
  }
  handleDelete = (e, selectedDocument) => {
    if (e && e.preventDefault) e.preventDefault();
    this.setState({
      showConfirm: true,
      selectedDocument,
    });
  }

  handleCancelDelete = () => {
    this.setState({
      showConfirm: false,
      selectedDocument: null,
    });
  }

  handleConfirmDelete = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const { selectedDocument } = this.state;

    this.handleCancelDelete();
    if (selectedDocument) {
      this.deleteDocument(selectedDocument.id);
    }
  }
  downloadDocument = (id, fileName) => {
    const downloadUrl = `${config.get('bookApiURL')}/shared_medias/${id}/file/${fileName}?dl=true`;

    const link = document.createElement('a');
    link.href = downloadUrl;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deleteDocument = async (id) => {
    const { store } = this.context;
    const { mediaToken, t } = this.props;

    try {
      const res = await store.dispatch(fetchM(`/shared_medias/${id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': mediaToken,
        },
      }));

      if (!res.ok) {
        const message = await res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }

      store.dispatch(flashSuccessMessage({
        header: t('Delete Success', { ns: TRANSLATION_FILES.translation }),
        message: t('Document successfully deleted', { ns: TRANSLATION_FILES.translation }),
      }));

      if (this.props.fetchDocument) {
        this.props.fetchDocument();
      }
    } catch (err) {
      store.dispatch(flashErrorMessage(err));
    }
  }

  handleFilterOnChange = (value, queryName) => {
    const query = decodeSearch(this.props.location.search);
    if (value === 'all') {
      delete query[queryName];
    } else {
      query[queryName] = value;
    }
    this.setState({ [queryName]: value }, () => {
      this.replaceQuery(query);
    });
  }
  handleClearFilter = (queryName) => {
    const query = decodeSearch(this.props.location.search);
    delete query[queryName];
    this.setState({ [queryName]: '' }, () => {
      this.replaceQuery(query);
    });
  }
  documentQueryKey = query => JSON.stringify([this.getParent(), query]);

  async replaceQuery(query, updateHistory = true) {
    const { store } = this.context;
    const { parentType, parentID } = this.getParent();

    const requestVersion = ++this.documentRequestVersion;
    const documentDataKey = this.documentQueryKey(query);
    const isCurrent = () => this.mounted && requestVersion === this.documentRequestVersion;
    // A blank parent_id must never issue an unscoped read.
    if (!parentID) return;
    this.pendingDocumentDataKey = documentDataKey;
    const queryString = encodeSearch(query);
    // Controls and browser history describe the intended query immediately.
    if (updateHistory) store.dispatch(replace(`${this.props.location.pathname}?${queryString}`));

    try {
      // Keep the committed query's rows until a new fetch succeeds. Render already
      // hides cached rows when navigation changes the parent or query.
      this.setState({ loading: true, documentLoadError: false });

      let url = `/shared_medias/?parent_type=${parentType}&parent_id=${parentID}`;

      if (queryString) {
        url += `&${queryString}`;
      }
      const res = await store.dispatch(fetchM(url, {
        method: 'GET',
        credentials: 'include',
      }));

      if (!isCurrent()) return;
      if (res && res.ok) {
        const documentData = await res.json();
        if (isCurrent()) {
          this.setState({
            documentData: documentData || [],
            documentDataKey,
            loading: false,
          });
        }
      } else if (isCurrent()) { this.setState({ loading: false, documentLoadError: true }); }
    } catch (err) {
      if (isCurrent()) this.setState({ loading: false, documentLoadError: true });
    } finally {
      if (isCurrent()) this.pendingDocumentDataKey = null;
    }
  }
  renderFileType(fileType) {
    const { t } = this.props;
    // CPI-1249 (A22): name every type book-api can store, so a generated sales form is not "N/A".
    const label = getDocumentTypeLabel(fileType);
    return t(label || 'N/A', { ns: TRANSLATION_FILES.translation });
  }
  render() {
    const { loading, t, auth } = this.props;
    const currentQueryKey = this.documentQueryKey(decodeSearch((this.props.location && this.props.location.search) || ''));
    const rawData = this.state.documentDataKey ?
      (this.state.documentDataKey === currentQueryKey ? this.state.documentData || [] : []) : (this.initialDocumentDataKey === currentQueryKey ? (this.props.documentData || []).filter(doc =>
        doc.parentType === this.getParent().parentType && String(doc.parentID) === String(this.getParent().parentID)) : []);
    const isVietnam = isVietnamCompany(auth);
    const opportunity = this.props.opportunity || this.props.data;
    const parent = this.getParent();
    // Bookings can retain a previous opportunity while its next booking loads. Snapshot rows are
    // valid only for the document parent currently rendered, never for a merely scoped URL.
    const currentOpportunity = opportunity && parent.parentType === 'opportunity' &&
      String(opportunity.id) === String(parent.parentID) ? opportunity : null;
    const currentIdentityMode = isVietnam && !!this.props.currentIdentityMode;
    const snapshotReady = !currentIdentityMode && isVietnam && snapshotIsReady(currentOpportunity);
    const snapshotDocuments = !currentIdentityMode && isVietnam ?
      getSnapshotIdentityDocuments(currentOpportunity, config.get('bookApiURL')) : [];
    const currentIdentityDocuments = currentIdentityMode ? (this.props.currentIdentityDocuments || []) : [];
    const sharedDocuments = this.getUniqueDocuments(rawData).filter(doc => (
      !(snapshotReady || currentIdentityMode) || !isGeneratedIdentityMirror(doc)
    ));
    const query = decodeSearch((this.props.location && this.props.location.search) || '');
    // Query state is authoritative: the browser Back button must restore unfiltered snapshot rows.
    const search = String(query.search || '').trim().toLowerCase();
    const fileType = query.file_type || '';
    const protectedIdentityDocuments = snapshotDocuments.concat(currentIdentityDocuments);
    const filteredProtectedIdentityDocuments = protectedIdentityDocuments.filter((doc) => {
      if (fileType && fileType !== 'all' && doc.fileType !== fileType) return false;
      if (!search) return true;
      return [doc.fileName, doc.purchaserName, doc.snapshotLabel, doc.currentIdentityLabel]
        .some(value => value && String(value).toLowerCase().includes(search));
    });
    const data = sharedDocuments.concat(filteredProtectedIdentityDocuments);
    // CPI-1249: only Vietnam has rows of the generated types, so only its dropdown filters them.
    const typeFilterTypes = isVietnam ? DOCUMENT_TYPE_LABELS : DOCUMENT_TYPE_OPTIONS;
    const filterOptions = [
  { text: t('Type: All', { ns: TRANSLATION_FILES.translation }), value: 'all' },
      ...typeFilterTypes.map(opt => ({
        ...opt,
        text: t(opt.text, { ns: TRANSLATION_FILES.translation }),
      })),
    ];

    return (
      <div style={{ padding: '1rem' }}>
        {/* Calling the AddDocument Card/Modal */}
        <AddDocument
          type="project"
          open={this.state.showAddMedia}
          onClose={this.handleCloseAddMedia}
          onSubmit={this.handleOnUpload}
          id={this.props.id}
          token={this.props.mediaToken}
          scrolling
        />

        {auth && auth.isHeadquarters && (
          <DeleteConfirm
            open={this.state.showConfirm}
            name={this.state.selectedDocument ? this.state.selectedDocument.fileName : ''}
            onClose={this.handleCancelDelete}
            onConfirm={this.handleConfirmDelete}
          />
        )}

        <ViewLarge
          open={this.state.showLarge}
          onClose={this.handleCloseLarge}
          picture={this.state.selectedMedia}
          src={this.state.selectedMedia ? this.state.selectedMedia.url : undefined}
        />

        {/* Top Header Row */}
        <div className="document-header">
          <div>
            <Header as="h2" className="header">{t('Document uploaded', { ns: TRANSLATION_FILES.screens })}</Header>
            <span className="span">{data.length} {t('found', { ns: TRANSLATION_FILES.screens })}</span>
          </div>
          {/*
          <div style={{
            display: 'flex', alignItems: 'center', gap: '15px',
          }}>
            <Label basic style={{ border: 'none' }}>
              <Icon name="file alternate outline" /> {t('Pending verification', { ns: TRANSLATION_FILES.screens })} <Icon name="dropdown" />
            </Label>
            <Button color="teal" basic icon labelPosition="left">
              <Icon name="check" />
              {t('Mark as verified', { ns: TRANSLATION_FILES.screens })}
            </Button>
          </div>
          */}
        </div>

        {/* Filters Row */}
        {/* search */}
        <Grid stackable className="document-search-container">
          <Grid.Column width={4}>
            <SearchFilter
              isLoading={this.state.loading}
              onChange={this.handleFilterOnChange}
              onClose={this.handleClearFilter}
              search={query.search || ''}
              placeholder={t('Search document', { ns: TRANSLATION_FILES.translation })}
            />
          </Grid.Column>

          {/* Filter by Type */}
          <Grid.Column width={3}>
            <Select
              placeholder={t('Type: All', { ns: TRANSLATION_FILES.translation })}
              options={filterOptions}
              value={fileType || 'all'}
              onChange={(e, { value }) => this.handleFilterOnChange(value, 'file_type')}
              fluid
            />
          </Grid.Column>
          {/* <Grid.Column width={3}>
            <Select placeholder="Shared by: All" options={[{text: 'All', value: 'all'}]} fluid />
          </Grid.Column>*/}
          <Grid.Column width={2} floated="right">
            <Button
              color="teal"
              fluid
              onClick={this.handleShowAddMedia}
              className="document-upload-button"
            >
              <img src={uploadIcon} className="document-upload-icon" alt="uploadIcon" />{t('Upload', { ns: TRANSLATION_FILES.screens })}
            </Button>
          </Grid.Column>
        </Grid>

        {/* Table Section */}
        <Table basic="very" className="document-outline-table" loading={loading}>
          <Table.Header>
            <Table.Row className="document-table-header">
              <Table.HeaderCell>{t('Name', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Date added', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Type', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Uploaded by', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Shared by', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right" />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {this.state.documentLoadError && (
              <Table.Row><Table.Cell colSpan="6" textAlign="center" role="alert">
                <p>{t('Unable to load documents. Please try again.')}</p>
                <Button onClick={() => this.replaceQuery(query, false)}>{t('Retry')}</Button>
              </Table.Cell></Table.Row>
            )}
            {data.length > 0 ? (
            data.map((doc, index) => (
              <Table.Row key={this.isProtectedIdentityDocument(doc) ? doc.id : (doc.id || index)}>
                <Table.Cell>
                  <div className="document-name-row">
                    <Icon name={this.getFileIcon(doc.fileName)} size="large" color="teal" />
                    <div className="doc-name-container">
                      {this.isProtectedIdentityUnavailable(doc) ? (
                        <span className="doc-name">{doc.fileName}</span>
                      ) : doc.fileName ? (
                        <a
                          className="doc-name"
                          href={this.isProtectedIdentityDocument(doc) ? this.protectedIdentityURL(doc) : this.getDocumentURL(doc)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => this.handleOpenDocument(e, doc)}
                        >
                          {doc.currentIdentityDocument && doc.currentIdentityLabel ?
                            `${doc.fileName} · ${doc.currentIdentityLabel}` : doc.fileName}
                        </a>
                      ) : (
                        <span className="doc-name">Untitled</span>
                      )}
                      {doc.snapshotDocument && (
                        <div className="doc-context">
                          {doc.purchaserName} {' · '}{doc.snapshotLabel}
                        </div>
                      )}
                      <div className="doc-size">
                        {doc.currentIdentityDocument ?
                          (Number.isFinite(doc.fileSizeBytes) ? this.formatBytes(doc.fileSizeBytes) : '-') :
                          (this.isProtectedIdentityDocument(doc) ? '-' : (this.formatBytes(doc.fileSizeBytes) || '1 MB'))}
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell data-label={t('Date added', { ns: TRANSLATION_FILES.screens })}>
                  {doc.createdAt ? <Timestamp format="MMM D, YYYY [at] HH:mm" timestamp={doc.createdAt} /> : '-'}
                </Table.Cell>
                <Table.Cell data-label={t('Type', { ns: TRANSLATION_FILES.screens })}>{this.renderFileType(doc.fileType)}</Table.Cell>
                <Table.Cell data-label={t('Uploaded by', { ns: TRANSLATION_FILES.screens })}>{doc.currentIdentityDocument ? (doc.uploaderName || '-') : (this.isProtectedIdentityDocument(doc) ? '-' : (doc.uploaderName || 'System'))}</Table.Cell>
                <Table.Cell data-label={t('Shared by', { ns: TRANSLATION_FILES.screens })}>{doc.currentIdentityDocument ? (doc.shared_by || '-') : (this.isProtectedIdentityDocument(doc) ? '-' : (doc.shared_by || '-'))}</Table.Cell>
                <Table.Cell textAlign="right">
                  {!this.isProtectedIdentityUnavailable(doc) && (
                    <Button className="btn-icon-only" aria-label="Download" onClick={e => this.handleDownload(e, doc)}>
                      <img src={downloadIcon} alt="download" />
                    </Button>
                  )}
                  {!this.isProtectedIdentityDocument(doc) && auth && auth.isHeadquarters && (
                    <Button className="btn-icon-only" aria-label="Delete" onClick={e => this.handleDelete(e, doc)}>
                      <img src={deleteIcon} alt="delete" />
                    </Button>
                  )}
                  {/* <Button icon="share alternate" basic circular /> */}
                </Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell colSpan="6" textAlign="center" className="document-not-found">
                <Icon name="search" size="large" />
                {!this.state.documentLoadError && <p>{t(this.state.loading ? 'Loading' : 'No documents match your search', { ns: TRANSLATION_FILES.screens })}</p>}
              </Table.Cell>
            </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export default withTranslation(namespaces)(Documents);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/Document.jsx